/**
 * EN-1492: the single flow every generation trigger uses. Save the open document and await it,
 * ask the server which edits exist, then show the keep-or-discard modal, or generate straight away
 * when nothing was edited. Stop & Generate asks through the same modal, but its generation is already
 * running, so the answer goes to the API instead (D18-D23).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
    answerEditChoices,
    EditChoice,
    EditRowsDocument,
    fetchEditRowsWithLatest,
    LatestVersion,
    PendingEditChoice,
    prepareReleaseOnExit,
    registerPendingEditChoices,
    releaseEditChoices,
} from "@/common/lib/api/documentEdits";
import { normalizePetName } from "./choicesForPatient";
import KeepEditsModal from "./KeepEditsModal";
import {
    ALREADY_WRITTEN_MESSAGE,
    ANSWER_FAILED_MESSAGE,
    BUSY_MESSAGE,
    CHECK_FAILED_KEPT_ALL_MESSAGE,
    STOP_DROPPED_KEPT_ALL_MESSAGE,
    STOP_SAVE_FAILED_MESSAGE,
    KEEP_EDITS_COPY,
    TRIGGER_COPY,
    KEEP_EDITS_TOAST_IDS,
    type KeepEditsTrigger,
} from "./keepEditsCopy";

// A check slower than this gets a loading toast, so the click is not met with silence.
const CHECKING_TOAST_DELAY_MS = 500;
// A request dropped by the busy guard sooner than this after the guard was taken is a double-click: no toast.
const BUSY_TOAST_AFTER_MS = 800;
// The API's PENDING_EDIT_CHOICE_TTL_MS (framework/cache/pendingEditChoices.ts): after it the entries are gone.
const PENDING_EDIT_CHOICE_TTL_MS = 10 * 60 * 1000;

/**
 * EN-1564: until disarmed, a reload or close (pagehide) releases the registered entries, so their generations
 * keep all at once instead of waiting out D21's 2 minutes for an answer that will never come. The request is prepared
 * now and sent synchronously from pagehide (apiFetch would never leave a closing page). It is ready only once the
 * register has settled too, so the release never reaches the API before the entries it releases. A pagehide before
 * then sends nothing: the 2-minute wait, as before. Not on a bfcache entry, since the page and its dialog may come
 * back.
 * Disarms itself after the server's TTL: D21's wait starts only after upload and transcription, so 2 minutes from the
 * press is too soon. The bound narrows (does not close) the window in which a release could delete a colleague's newer
 * Stop on the same visit (keyed by visit, type and pet; keep-all, so nothing is lost).
 */
const armReleaseOnExit = (
    p: { patientRecordId: string; documents: PendingEditChoice[] },
    registered: Promise<unknown>,
): (() => void) => {
    let send: (() => void) | null = null;
    let armed = true;
    // Never lets a failure reach askAtStop.
    Promise.resolve()
        .then(() => Promise.all([prepareReleaseOnExit(p), registered]))
        .then(([sender]) => {
            if (armed) send = sender;
        })
        .catch(() => undefined);
    const onPageHide = (event: PageTransitionEvent) => {
        if (event.persisted) return;
        send?.();
        disarm();
    };
    const timer = setTimeout(() => disarm(), PENDING_EDIT_CHOICE_TTL_MS);
    const disarm = () => {
        armed = false;
        send = null;
        clearTimeout(timer);
        window.removeEventListener("pagehide", onPageHide);
    };
    window.addEventListener("pagehide", onPageHide);
    return disarm;
};

/** Starts the delayed "Checking your edits..." toast; the returned stop cancels it and dismisses it if shown. */
export const startCheckingToast = (): (() => void) => {
    const timer = setTimeout(
        () => toast.loading(KEEP_EDITS_COPY.checkingToast, { toastId: KEEP_EDITS_TOAST_IDS.checking }),
        CHECKING_TOAST_DELAY_MS,
    );
    return () => {
        clearTimeout(timer);
        toast.dismiss(KEEP_EDITS_TOAST_IDS.checking);
    };
};

/**
 * The edits check's retry: the app's default, except a 4xx (other than 408 and 429), which fails the same way every
 * time (e.g. the API's 400 for too many types times pets), so retrying only delays the kept-all notice.
 */
const editsCheckRetry =
    (queryClient: QueryClient) =>
    (failureCount: number, error: Error): boolean => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429) return false;
        const fallback = queryClient.getDefaultOptions().queries?.retry;
        if (typeof fallback === "function") return !!fallback(failureCount, error);
        if (typeof fallback === "number") return failureCount < fallback;
        return fallback === true;
    };

export type GenerationRequest = {
    /** "auto" picks the copy from the document types and the selected inputs (R17). */
    trigger: KeepEditsTrigger | "auto";
    patientRecordId: string;
    documentTypeIds: string[];
    patientNames?: string[];
    selectedInputIds?: string[];
    /** Limits the edits considered to this section and its nested fields. */
    sectionId?: string;
    /** The document being regenerated; its version supplies the rows even when it is not the latest. */
    chatCompletionId?: string;
    /**
     * The version open in the editor, for Generate and Retry (not Regenerate). If the server's latest version of
     * that type and pet is a different document, the open one is stale (realtime dropped), so nothing is generated.
     */
    openDocument?: { id: string; documentTypeId: string; patientName?: string | null };
    /**
     * `null` means there is nothing to keep, so generate as usual. `checkFailed` marks the one `null` that is a guess:
     * the edits check failed, so the server may keep edits this caller never saw (otherwise `null` means none).
     */
    onGenerate: (choices: EditChoice[] | null, info?: { checkFailed: boolean }) => void;
};

/** Stop & Generate on a recording of this visit, for the types (and pets) it will generate. */
export type StopRequest = {
    patientRecordId: string;
    documentTypeIds: string[];
    patientNames?: string[];
};

type KeepEditsContext = {
    requestGeneration: (p: GenerationRequest) => Promise<void>;
    /** D18-D23: asks about edits while the recording uploads; the answer goes to the API. Never throws. */
    askAtStop: (p: StopRequest) => Promise<void>;
    /** Registers the open document's awaited save; nothing registered means nothing to save. Returns an
     *  unregister that clears the slot only while it still holds this save, so a new editor that registers
     *  before the old one's cleanup runs is never cleared by that cleanup. */
    registerFlush: (fn: () => Promise<void>) => () => void;
    /** Awaits the open document's registered save; resolves at once when none is registered. Rethrows the save's
     *  error unchanged. Takes no busy guard and opens no modal. */
    flushOpenDocument: () => Promise<void>;
    /** Runs `fn` under the same busy guard as requestGeneration, for flows that ask the vet themselves (the leaf
     *  regenerate confirm). Resolves undefined without calling `fn` when a check or modal is already up; while
     *  `fn` is pending, requestGeneration is dropped. */
    runExclusive: <T>(fn: () => Promise<T>) => Promise<T | undefined>;
};

/** One visit (PatientView's provider, keyed by the visit) or the app (the outer provider). */
type Scope = object;

type OpenModal = {
    /** The visit that opened it, or null for Stop & Generate, which outlives navigation. */
    scope: Scope | null;
    trigger: KeepEditsTrigger;
    documents: EditRowsDocument[];
    sectionName?: string;
    confirmLabel?: string;
    onGenerate: GenerationRequest["onGenerate"];
    /** Stop & Generate: closing answers keep all, so there is no Cancel (D22). */
    onCancel?: () => void;
    showCancel?: boolean;
};

const KeepEditsCtx = createContext<KeepEditsContext | null>(null);

type KeepEditsRootApi = Omit<KeepEditsContext, "requestGeneration"> & {
    requestGeneration: (p: GenerationRequest, scope: Scope) => Promise<void>;
    enterScope: (scope: Scope) => void;
    /** Closes the scope's dialog as Cancel and frees the busy guard from its check, as an unmount once did. */
    leaveScope: (scope: Scope) => void;
};
const KeepEditsRootCtx = createContext<KeepEditsRootApi | null>(null);

const resolveTrigger = (p: GenerationRequest, documents: EditRowsDocument[]): KeepEditsTrigger => {
    if (p.trigger !== "auto") return p.trigger;
    if (p.documentTypeIds.length > 1) return "generateSeveral";
    const hasNewInput = (p.selectedInputIds ?? []).some((id) =>
        documents.some((d) => !d.inputs.transcriptSectionIds.includes(id) && !d.inputs.documentAssetIds.includes(id)),
    );
    return hasNewInput ? "newRecording" : "retry";
};

const filterToSection = (documents: EditRowsDocument[], sectionId: string): EditRowsDocument[] =>
    documents
        .map((d) => ({ ...d, rows: d.rows.filter((r) => r.id === sectionId || r.sectionId === sectionId) }))
        .filter((d) => d.rows.length > 0);

// The section's own row, or (when only its nested fields were edited) a field's parent name.
const sectionNameOf = (documents: EditRowsDocument[], sectionId: string): string | undefined => {
    const rows = documents.flatMap((d) => d.rows);
    return rows.find((r) => r.id === sectionId)?.name ?? rows.find((r) => r.sectionName)?.sectionName;
};

// Every edit kept: what generation does when nobody answers (D5).
const keepAllChoices = (documents: EditRowsDocument[]): EditChoice[] =>
    documents.map((d) => ({
        documentTypeId: d.documentTypeId,
        patientName: d.patientName,
        sourceChatCompletionId: d.sourceChatCompletionId,
        keepRowIds: d.rows.map((r) => r.id),
    }));

type ProviderProps = {
    children: React.ReactNode;
    /** The visit: a dialog opened through this provider closes as Cancel when it changes or the provider unmounts. */
    scopeKey?: string;
};

/**
 * Mounted app-wide (PermissionWrapper, so the recording widget can ask) and again by PatientView, keyed by the visit,
 * which is what makes a visit's dialogs close when the vet leaves it (it also serves trees without the outer one, such
 * as tests). A nested provider defers to the outer one, so every trigger shares one busy guard and one modal, but it
 * keeps its own scope.
 */
export const KeepEditsProvider: React.FC<ProviderProps> = ({ children, scopeKey }) => {
    const scoped = <KeepEditsScope scopeKey={scopeKey}>{children}</KeepEditsScope>;
    return useContext(KeepEditsRootCtx) ? scoped : <KeepEditsRoot>{scoped}</KeepEditsRoot>;
};

const KeepEditsScope: React.FC<ProviderProps> = ({ children, scopeKey }) => {
    const root = useContext(KeepEditsRootCtx)!;
    // A new visit is a new scope; leaving the old one closes its dialog.
    const scope = useMemo<Scope>(() => ({}), [scopeKey]);
    useEffect(() => {
        root.enterScope(scope);
        return () => root.leaveScope(scope);
    }, [root, scope]);
    const value = useMemo<KeepEditsContext>(
        () => ({
            requestGeneration: (p) => root.requestGeneration(p, scope),
            askAtStop: root.askAtStop,
            registerFlush: root.registerFlush,
            flushOpenDocument: root.flushOpenDocument,
            runExclusive: root.runExclusive,
        }),
        [root, scope],
    );
    return <KeepEditsCtx.Provider value={value}>{children}</KeepEditsCtx.Provider>;
};

const KeepEditsRoot: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const queryClient = useQueryClient();
    const flushRef = useRef<(() => Promise<void>) | null>(null);
    // The running check, if any. A token, so a check whose scope was left cannot release a newer one's guard.
    const inFlightRef = useRef<{ scope: Scope | null } | null>(null);
    // Mirrors `modal`, so a trigger fired while the modal is open is ignored rather than replacing it.
    const modalRef = useRef<OpenModal | null>(null);
    const liveScopesRef = useRef(new Set<Scope>());
    // EN-1564: the open Stop dialog's release on page exit. An unmount disarms it (no pagehide fires on a route
    // change), so those generations wait out D21's 2 minutes.
    const disarmExitRef = useRef<(() => void) | null>(null);
    useEffect(() => () => disarmExitRef.current?.(), []);
    // Set once per request: the modal resets its choices whenever `documents` changes identity.
    const [modal, setModalState] = useState<OpenModal | null>(null);
    const setModal = useCallback((next: OpenModal | null) => {
        modalRef.current = next;
        setModalState(next);
    }, []);

    // When the current busy stretch began: a busy stretch always starts with a take (a dialog follows its check).
    const busySinceRef = useRef(0);
    // The shared busy guard: null when a check or a dialog is already up.
    const take = useCallback((scope: Scope | null) => {
        if (inFlightRef.current || modalRef.current) return null;
        const token = { scope };
        inFlightRef.current = token;
        busySinceRef.current = Date.now();
        return token;
    }, []);
    // A dropped request says why, except a plain double-click (the guard taken moments ago), which stays silent.
    const takeOrSayBusy = useCallback(
        (scope: Scope | null) => {
            const token = take(scope);
            if (!token && Date.now() - busySinceRef.current > BUSY_TOAST_AFTER_MS) {
                toast.info(BUSY_MESSAGE, { toastId: KEEP_EDITS_TOAST_IDS.busy });
            }
            return token;
        },
        [take],
    );
    const release = useCallback((token: { scope: Scope | null }) => {
        if (inFlightRef.current === token) inFlightRef.current = null;
    }, []);

    const enterScope = useCallback((scope: Scope) => {
        liveScopesRef.current.add(scope);
    }, []);
    const leaveScope = useCallback(
        (scope: Scope) => {
            liveScopesRef.current.delete(scope);
            if (modalRef.current?.scope === scope) setModal(null);
            if (inFlightRef.current?.scope === scope) inFlightRef.current = null;
        },
        [setModal],
    );

    const registerFlush = useCallback((fn: () => Promise<void>) => {
        flushRef.current = fn;
        return () => {
            if (flushRef.current === fn) flushRef.current = null;
        };
    }, []);

    const flushOpenDocument = useCallback(async () => {
        await flushRef.current?.();
    }, []);

    // Held across the leaf confirm on purpose: that dialog is modal, so every other Generate/Retry is behind its
    // overlay anyway; the lock matters for the window before a dialog is up (a slow check), where two flows could
    // otherwise each open one. Cancel, Confirm and unmount all release it.
    const runExclusive = useCallback(
        async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
            const token = takeOrSayBusy(null);
            if (!token) return undefined;
            try {
                return await fn();
            } finally {
                release(token);
            }
        },
        [takeOrSayBusy, release],
    );

    const requestGeneration = useCallback(
        async (p: GenerationRequest, scope: Scope) => {
            // Double-click and open-modal guard. A request dropped within BUSY_TOAST_AFTER_MS of the guard being
            // taken is a double-click and stays silent; later, the vet is told to finish the current step, since a
            // dialog's overlay does not cover every trigger (the recording widget) and a check can be slow (EN-1492).
            const token = takeOrSayBusy(scope);
            if (!token) return;
            // Covers the save and the edits check together; any outcome (modal, generate, error) dismisses it.
            const stopChecking = startCheckingToast();
            try {
                try {
                    await flushRef.current?.();
                } catch (error) {
                    toast.error(error instanceof Error ? error.message : "Your edits could not be saved. Try again.", {
                        toastId: KEEP_EDITS_TOAST_IDS.saveFailed,
                    });
                    return;
                }

                let documents: EditRowsDocument[];
                let latestVersions: LatestVersion[] | undefined;
                try {
                    const fetched = await queryClient.fetchQuery({
                        queryKey: [
                            "editRows",
                            p.patientRecordId,
                            p.documentTypeIds,
                            p.patientNames,
                            p.chatCompletionId,
                        ],
                        queryFn: () =>
                            fetchEditRowsWithLatest({
                                patientRecordId: p.patientRecordId,
                                documentTypeIds: p.documentTypeIds,
                                patientNames: p.patientNames,
                                chatCompletionId: p.chatCompletionId,
                            }),
                        staleTime: 0,
                        retry: editsCheckRetry(queryClient),
                    });
                    documents = fetched.documents;
                    latestVersions = fetched.latestVersions;
                } catch {
                    // The check failed after React Query's retries (a 404 already resolved to no documents in
                    // fetchEditRowsWithLatest): generate anyway, keeping every edit (no choices), rather than blocking
                    // generation on this one endpoint (GLaDOS HIGH). Deliberate (specs/en-1492/READY.md ruling 16):
                    // failing toward keeping the vet's text never loses it, and the toast says so; failing toward
                    // discard, or blocking, would during an outage either lose edits or stop every generation. A vet
                    // who wanted a discard can Retry once it recovers.
                    toast.info(CHECK_FAILED_KEPT_ALL_MESSAGE, { toastId: KEEP_EDITS_TOAST_IDS.checkFailedKeptAll });
                    p.onGenerate(null, { checkFailed: true });
                    return;
                }

                const open = p.openDocument;
                // latestVersions names the newest version per type and pet, including one with no edits. Every
                // server with editRows sends it (a server without the route 404s, which fetchEditRowsWithLatest
                // turns into no documents and no versions); without it there is no "newest" to compare with.
                const newer =
                    open && latestVersions
                        ? latestVersions.find(
                              (v) =>
                                  v.documentTypeId === open.documentTypeId &&
                                  normalizePetName(v.patientName) === normalizePetName(open.patientName) &&
                                  v.chatCompletionId !== open.id,
                          )
                        : undefined;
                if (newer) {
                    toast.error(
                        `A newer version of ${newer.header} exists. Open it to choose what happens to your edits.`,
                        { toastId: KEEP_EDITS_TOAST_IDS.newerVersion },
                    );
                    return;
                }

                if (p.sectionId) documents = filterToSection(documents, p.sectionId);
                if (documents.length === 0) {
                    p.onGenerate(null);
                    return;
                }
                // The visit closed while checking: its dialog would have unmounted with it.
                if (!liveScopesRef.current.has(scope)) return;
                const trigger = resolveTrigger(p, documents);
                setModal({
                    scope,
                    trigger,
                    // Spec copy table: sidebar Generate with no new input uses the Retry copy, but its button says Generate.
                    confirmLabel:
                        p.trigger === "auto" && trigger === "retry" ? TRIGGER_COPY.newRecording.confirm : undefined,
                    documents,
                    sectionName: p.sectionId ? sectionNameOf(documents, p.sectionId) : undefined,
                    onGenerate: p.onGenerate,
                });
            } finally {
                stopChecking();
                release(token);
            }
        },
        [queryClient, setModal, takeOrSayBusy, release],
    );

    const askAtStop = useCallback(
        async (p: StopRequest) => {
            // Same guard as requestGeneration. With a check or dialog already up, this recording keeps all (D5),
            // and the vet is told so rather than never being asked.
            const token = take(null);
            if (!token) {
                toast.info(STOP_DROPPED_KEPT_ALL_MESSAGE, { toastId: KEEP_EDITS_TOAST_IDS.stopDroppedKeptAll });
                return;
            }
            // D20: registered at the press, before the save, the check and the upload, for every type and pet this
            // recording generates (one per type on a single-pet visit). Never awaited here: a failed register still
            // asks, and the answer then reports "already written" if it is too late (D23).
            const registered: PendingEditChoice[] = p.documentTypeIds.flatMap((documentTypeId) =>
                (p.patientNames?.length ? p.patientNames : [null]).map((patientName) => ({
                    documentTypeId,
                    patientName,
                })),
            );
            const keyOf = (d: { documentTypeId: string; patientName?: string | null }) =>
                `${d.documentTypeId}|${normalizePetName(d.patientName)}`;
            // Residual gap (D20/D23): a register that reaches the server after the generation already looked finds no
            // one to wait for it, so that document keeps all, and the answer can report applied without the toast. It
            // needs the register to be slower than upload plus transcription; the API's stale-source check bounds any
            // later reuse of that answer. Keying entries by the recording would close it (an API and Transcribe change).
            const pending = registerPendingEditChoices({ patientRecordId: p.patientRecordId, documents: registered });
            // EN-1564: releasing everything registered is safe: answered entries stay, released ones are no-ops.
            const disarmExit = armReleaseOnExit({ patientRecordId: p.patientRecordId, documents: registered }, pending);
            disarmExitRef.current = disarmExit;
            let dialogOpened = false;
            // Entries with nothing to ask keep all at once rather than waiting out D21's bound. After the register
            // settles, so the release never reaches the API before the entry it releases.
            // A release that fails (or an older API's 404) leaves its entries pending; the server's 10-minute TTL
            // bounds them (D20), and a generation waits at most D21's 2 minutes before keeping all.
            const releaseUnasked = (asked: Set<string>) => {
                const unasked = registered.filter((d) => !asked.has(keyOf(d)));
                if (unasked.length === 0) return;
                void pending.then(() => releaseEditChoices({ patientRecordId: p.patientRecordId, documents: unasked }));
            };
            try {
                try {
                    await flushRef.current?.();
                } catch {
                    // The recording is generating whatever happens here, so ask about the edits that were saved, and
                    // say the unsaved ones may be missing (Generate would block instead; this generation cannot).
                    toast.error(STOP_SAVE_FAILED_MESSAGE, { toastId: KEEP_EDITS_TOAST_IDS.saveFailed });
                }

                let documents: EditRowsDocument[];
                try {
                    const fetched = await queryClient.fetchQuery({
                        queryKey: ["editRows", p.patientRecordId, p.documentTypeIds, p.patientNames, undefined],
                        queryFn: () =>
                            fetchEditRowsWithLatest({
                                patientRecordId: p.patientRecordId,
                                documentTypeIds: p.documentTypeIds,
                                patientNames: p.patientNames,
                            }),
                        staleTime: 0,
                        retry: editsCheckRetry(queryClient),
                    });
                    documents = fetched.documents;
                } catch {
                    // Generation keeps every edit once released; say so, as Generate does.
                    releaseUnasked(new Set());
                    toast.info(CHECK_FAILED_KEPT_ALL_MESSAGE, { toastId: KEEP_EDITS_TOAST_IDS.checkFailedKeptAll });
                    return;
                }
                // Only what was registered: an answer for anything else would reach no generation.
                const registeredKeys = new Set(registered.map(keyOf));
                documents = documents.filter((d) => registeredKeys.has(keyOf(d)));
                releaseUnasked(new Set(documents.map(keyOf)));
                if (documents.length === 0) return;

                const rowCounts = new Map(documents.map((d) => [d.sourceChatCompletionId, d.rows.length]));
                const discards = (c: EditChoice) =>
                    c.keepRowIds.length < (rowCounts.get(c.sourceChatCompletionId) ?? 0);
                const answer = async (choices: EditChoice[]) => {
                    // The answer must not reach the API before the pending entry it answers.
                    await pending;
                    const applied = await answerEditChoices({
                        patientRecordId: p.patientRecordId,
                        editChoices: choices,
                    });
                    // Only once the answer landed: a close while it is in flight may stop it leaving the page, and the
                    // release then ends the wait at once. One that does arrive first keeps its entry (the API keeps
                    // answered ones). A failed answer (null) leaves the entries pending, so the release stays armed.
                    if (applied !== null) disarmExit();
                    // A keep-all answer matches what is written either way, so it needs no toast.
                    if (applied === null) {
                        if (choices.some(discards))
                            toast.error(ANSWER_FAILED_MESSAGE, { toastId: KEEP_EDITS_TOAST_IDS.answerFailed });
                        return;
                    }
                    const tooLate = applied.some(
                        (a) =>
                            !a.applied &&
                            choices.some(
                                (c) =>
                                    c.documentTypeId === a.documentTypeId &&
                                    normalizePetName(c.patientName) === normalizePetName(a.patientName) &&
                                    discards(c),
                            ),
                    );
                    if (tooLate) toast.info(ALREADY_WRITTEN_MESSAGE, { toastId: KEEP_EDITS_TOAST_IDS.alreadyWritten });
                };
                setModal({
                    scope: null,
                    trigger: "newRecording",
                    documents,
                    showCancel: false,
                    onGenerate: (choices) => void answer(choices ?? keepAllChoices(documents)),
                    onCancel: () => void answer(keepAllChoices(documents)),
                });
                dialogOpened = true;
            } finally {
                // No dialog: releaseUnasked has queued a release for every entry, so there is nothing to release on exit.
                if (!dialogOpened) disarmExit();
                release(token);
            }
        },
        [queryClient, setModal, take, release],
    );

    const value = useMemo(
        () => ({
            requestGeneration,
            askAtStop,
            registerFlush,
            flushOpenDocument,
            runExclusive,
            enterScope,
            leaveScope,
        }),
        [requestGeneration, askAtStop, registerFlush, flushOpenDocument, runExclusive, enterScope, leaveScope],
    );

    return (
        <KeepEditsRootCtx.Provider value={value}>
            {children}
            {modal && (
                <KeepEditsModal
                    isOpen
                    trigger={modal.trigger}
                    documents={modal.documents}
                    sectionName={modal.sectionName}
                    confirmLabel={modal.confirmLabel}
                    showCancel={modal.showCancel}
                    onCancel={() => {
                        const { onCancel } = modal;
                        setModal(null);
                        onCancel?.();
                    }}
                    onConfirm={(choices) => {
                        const { onGenerate } = modal;
                        setModal(null);
                        onGenerate(choices);
                    }}
                />
            )}
        </KeepEditsRootCtx.Provider>
    );
};

// Without a provider (isolated editor tests) there is nothing to register with and no flow to run.
const NO_PROVIDER: KeepEditsContext = {
    requestGeneration: () => Promise.reject(new Error("useKeepEdits must be used within a KeepEditsProvider")),
    // Nothing to ask with: the recording generates keeping all edits, as before EN-1492.
    askAtStop: () => Promise.resolve(),
    registerFlush: () => () => {},
    flushOpenDocument: async () => {},
    runExclusive: (fn) => fn(),
};

export const useKeepEdits = (): KeepEditsContext => useContext(KeepEditsCtx) ?? NO_PROVIDER;
